import React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import ExpandableLinkListPrototype from '/react4xp/components/prototype/ExpandableLinkList'
import type { ExpandableLinkListItem } from '/react4xp/components/prototype/ExpandableLinkList/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

type LinkRow = {
	linkText?: string
	description?: string
	href?: string
	external?: boolean
	image?: { src: string; alt?: string }
}

type ExpandableLinkListPartData = {
	overline?: string
	links?: LinkRow[]
	// Emitted by the processor only when `links` is empty.
	rowCount?: number
}

function toExpandableLinkListItem(row: LinkRow): ExpandableLinkListItem {
	return {
		label: row.linkText || '',
		description: row.description,
		// The prototype types `url` as a required string and always emits `<a href={url}>`.
		// A row whose optional Link is empty must still render (I99X-343), and `''` would
		// resolve to the current page, so hand down `undefined` instead: React omits the
		// attribute and the anchor is inert and unfocusable. The cast marks a prop-contract
		// gap that belongs upstream - `url` should be optional in the prototype.
		url: row.href as unknown as string,
		isExternal: !!row.external,
		image: row.image ? { src: row.image.src, alt: row.image.alt || '' } : undefined,
	}
}

function emptyReason(rowCount: number | undefined): string {
	if (!rowCount) {
		return 'No link rows added yet.'
	}
	return `${rowCount} row(s) added, but none has a link text, description or image.`
}

export const ExpandableLinkList = ({ data, meta }: ComponentProps<PartData>) => {
	const { overline, links = [], rowCount } = (data || {}) as ExpandableLinkListPartData

	// An unconfigured part still has to render something in Content Studio, or the
	// editor cannot select it to fill it in. The prototype renders `null` for zero
	// links (see prototype/src/components/ExpandableLinkList/index.tsx), so keep the
	// same edit-mode guard used by the other stub-replacing Views (e.g. ContentBlock).
	if (links.length === 0) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="expandable-link-list"
				title="Expandable link list"
				heading={overline}
				reason={emptyReason(rowCount)}
			/>
		)
	}

	return <ExpandableLinkListPrototype overline={overline} links={links.map(toExpandableLinkListItem)} />
}
